import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExternalLink, Bot } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getSettings, saveSettings } from "@/services/settingsService";

const OPENAI_MODELS = [
  { value: "gpt-4o-mini", label: "GPT-4o Mini", description: "Rapido y economico (~$0.001/consulta)" },
  { value: "gpt-4o", label: "GPT-4o", description: "Mas capaz, mayor costo (~$0.01/consulta)" },
  { value: "gpt-4.1-mini", label: "GPT-4.1 Mini", description: "Ultima generacion mini, rapido" },
  { value: "gpt-4.1", label: "GPT-4.1", description: "Ultima generacion, maximo rendimiento" },
  { value: "gpt-4.1-nano", label: "GPT-4.1 Nano", description: "Ultra economico, respuestas basicas" },
  { value: "o4-mini", label: "o4 Mini", description: "Razonamiento avanzado, mas lento" },
] as const;

const SETTINGS_KEYS = ["platform_name", "contact_email", "allow_signup", "allow_guest_browse", "legal_tos", "legal_privacy", "legal_support_email", "legal_support_url", "allow_offline_downloads", "subscription_grace_period_days", "rag_enabled", "rag_openai_api_key", "rag_model", "rag_system_prompt"];

export default function SettingsPage() {
  const { toast } = useToast();
  const [platformName, setPlatformName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [tos, setTos] = useState("");
  const [privacy, setPrivacy] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [supportUrl, setSupportUrl] = useState("");
  const [allowSignup, setAllowSignup] = useState(false);
  const [allowGuestBrowse, setAllowGuestBrowse] = useState(false);
  const [allowOffline, setAllowOffline] = useState(true);
  const [graceDays, setGraceDays] = useState("7");
  const [ragEnabled, setRagEnabled] = useState(false);
  const [ragApiKey, setRagApiKey] = useState("");
  const [ragModel, setRagModel] = useState("gpt-4o-mini");
  const [ragSystemPrompt, setRagSystemPrompt] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getSettings(SETTINGS_KEYS).then((data) => {
      setPlatformName(data.platform_name || "Senda de Vida");
      setContactEmail(data.contact_email || "contacto@sendadevida.com");
      setAllowSignup(data.allow_signup === "true");
      setAllowGuestBrowse(data.allow_guest_browse === "true");
      setTos(data.legal_tos || "");
      setPrivacy(data.legal_privacy || "");
      setSupportEmail(data.legal_support_email || "");
      setSupportUrl(data.legal_support_url || "");
      setAllowOffline(data.allow_offline_downloads !== "false");
      setGraceDays(data.subscription_grace_period_days || "7");
      setRagEnabled(data.rag_enabled === "true");
      setRagApiKey(data.rag_openai_api_key || "");
      setRagModel(data.rag_model || "gpt-4o-mini");
      setRagSystemPrompt(data.rag_system_prompt || "");
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const onSave = async () => {
    try {
      setSaving(true);
      await saveSettings({
        platform_name: platformName,
        contact_email: contactEmail,
        allow_signup: String(allowSignup),
        allow_guest_browse: String(allowGuestBrowse),
        legal_tos: tos,
        legal_privacy: privacy,
        legal_support_email: supportEmail,
        legal_support_url: supportUrl,
        allow_offline_downloads: String(allowOffline),
        subscription_grace_period_days: String(Math.max(0, Math.min(90, parseInt(graceDays) || 7))),
        rag_enabled: String(ragEnabled),
        rag_openai_api_key: ragApiKey,
        rag_model: ragModel,
        rag_system_prompt: ragSystemPrompt,
      });
      toast({ title: "Configuración guardada", description: "Los cambios se han aplicado correctamente." });
    } catch {
      toast({ title: "Error", description: "No se pudo guardar la configuración.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const baseUrl = window.location.origin;

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-heading font-bold text-foreground">Configuración</h1>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="audio">Audio</TabsTrigger>
          <TabsTrigger value="legal">Legal y compliance</TabsTrigger>
          <TabsTrigger value="rag" className="gap-1.5">
            <Bot className="h-3.5 w-3.5" />
            IA / Chat
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="space-y-2">
                <Label>Nombre de la plataforma</Label>
                <Input
                  value={platformName}
                  onChange={(e) => setPlatformName(e.target.value)}
                  placeholder="Senda de Vida"
                  disabled={!loaded}
                />
                <p className="text-xs text-muted-foreground">Se muestra en la app móvil como título de la plataforma.</p>
              </div>
              <div className="space-y-2">
                <Label>Email de contacto</Label>
                <Input
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="contacto@sendadevida.com"
                  disabled={!loaded}
                />
                <p className="text-xs text-muted-foreground">Se muestra en la app como dirección de contacto general.</p>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={allowSignup} onCheckedChange={setAllowSignup} disabled={!loaded} />
                <div>
                  <Label>Permitir registro de nuevos usuarios</Label>
                  <p className="text-xs text-muted-foreground">Si está desactivado, solo usuarios existentes podrán iniciar sesión.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={allowGuestBrowse} onCheckedChange={setAllowGuestBrowse} disabled={!loaded} />
                <div>
                  <Label>Mostrar contenido gratuito sin registro</Label>
                  <p className="text-xs text-muted-foreground">Permite explorar el catálogo sin crear cuenta. Solo contenido marcado como gratuito.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audio" className="mt-4">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="flex items-center gap-3">
                <Switch checked={allowOffline} onCheckedChange={setAllowOffline} disabled={!loaded} />
                <div>
                  <Label>Permitir descargas offline</Label>
                  <p className="text-xs text-muted-foreground">Los usuarios podrán descargar audio para escuchar sin conexión.</p>
                </div>
              </div>

              <div className="space-y-2 pt-2 border-t">
                <Label>Días de gracia post-cancelación</Label>
                <Input
                  type="number"
                  min={0}
                  max={90}
                  step={1}
                  value={graceDays}
                  onChange={(e) => setGraceDays(e.target.value)}
                  disabled={!loaded}
                  className="max-w-[160px]"
                />
                <p className="text-xs text-muted-foreground">
                  Días que un usuario conserva acceso offline al contenido ya descargado después de cancelar su suscripción. Referencia: Spotify usa 30, Audible 7. Compras individuales no se ven afectadas (acceso permanente). Rango 0–90.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="legal" className="mt-4 space-y-4">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="space-y-2">
                <Label>Email de soporte</Label>
                <Input
                  value={supportEmail}
                  onChange={(e) => setSupportEmail(e.target.value)}
                  placeholder="soporte@sendadevida.com"
                  disabled={!loaded}
                />
                <p className="text-xs text-muted-foreground">Se muestra en la app como contacto de soporte.</p>
              </div>
              <div className="space-y-2">
                <Label>URL de soporte (opcional)</Label>
                <Input
                  value={supportUrl}
                  onChange={(e) => setSupportUrl(e.target.value)}
                  placeholder="https://www.sendadevida.us/contact"
                  disabled={!loaded}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-2 pt-6">
              <div className="flex items-center justify-between">
                <Label>Términos de Servicio</Label>
                <a
                  href={`${baseUrl}/legal/terms`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  Ver página pública <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <Textarea
                value={tos}
                onChange={(e) => setTos(e.target.value)}
                placeholder="Escribe aquí los términos de servicio de la plataforma..."
                rows={12}
                disabled={!loaded}
              />
              <p className="text-xs text-muted-foreground">Requerido por App Store y Play Store.</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-2 pt-6">
              <div className="flex items-center justify-between">
                <Label>Política de Privacidad</Label>
                <a
                  href={`${baseUrl}/legal/privacy`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  Ver página pública <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <Textarea
                value={privacy}
                onChange={(e) => setPrivacy(e.target.value)}
                placeholder="Escribe aquí la política de privacidad..."
                rows={12}
                disabled={!loaded}
              />
              <p className="text-xs text-muted-foreground">Requerida por Apple y Google para publicar en tiendas.</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rag" className="mt-4 space-y-4">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="flex items-center gap-3">
                <Switch checked={ragEnabled} onCheckedChange={setRagEnabled} disabled={!loaded} />
                <div>
                  <Label>Activar asistente IA (Chat RAG)</Label>
                  <p className="text-xs text-muted-foreground">Habilita el chat con IA en la app móvil. Los usuarios podrán hacer preguntas sobre el contenido de la biblioteca.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="space-y-2">
                <Label>API Key de OpenAI</Label>
                <div className="flex gap-2">
                  <Input
                    type={showApiKey ? "text" : "password"}
                    value={ragApiKey}
                    onChange={(e) => setRagApiKey(e.target.value)}
                    placeholder="sk-..."
                    disabled={!loaded}
                    className="font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="shrink-0"
                  >
                    {showApiKey ? "Ocultar" : "Mostrar"}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Se usa para generar embeddings (text-embedding-3-small), transcribir audio (Whisper) y responder preguntas (GPT). Obtener en platform.openai.com.</p>
              </div>
              <div className="space-y-2">
                <Label>Modelo de chat</Label>
                <Select value={ragModel} onValueChange={setRagModel} disabled={!loaded}>
                  <SelectTrigger className="font-mono text-sm">
                    <SelectValue placeholder="Selecciona un modelo" />
                  </SelectTrigger>
                  <SelectContent>
                    {OPENAI_MODELS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        <span className="font-semibold">{m.label}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{m.description}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Modelo de OpenAI para respuestas del chat. GPT-4o Mini es el recomendado por costo/calidad.</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-2 pt-6">
              <Label>System prompt personalizado (opcional)</Label>
              <Textarea
                value={ragSystemPrompt}
                onChange={(e) => setRagSystemPrompt(e.target.value)}
                placeholder="Eres un asistente espiritual sabio y amable que ayuda a los usuarios a explorar y comprender el contenido de la biblioteca Senda de Vida..."
                rows={8}
                disabled={!loaded}
              />
              <p className="text-xs text-muted-foreground">Instrucciones personalizadas para el tono y comportamiento del asistente. Si se deja vacío, se usa el prompt por defecto.</p>
            </CardContent>
          </Card>

          {ragEnabled && !ragApiKey && (
            <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3">
              <p className="text-sm text-yellow-800">
                El chat está activado pero falta la API Key de OpenAI. Los usuarios verán un error al intentar usar el chat.
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Button variant="default" onClick={onSave} disabled={saving || !loaded}>
        {saving ? "Guardando..." : "Guardar cambios"}
      </Button>
    </div>
  );
}
